<script lang="ts">
  import { round, generateMagicIntervals as _generateMagicIntervals } from '@/Util/utils';
  import { _ } from 'svelte-i18n';
  import { format_duration } from '@/time';
  import { cn } from '@/lib/utils';
  import Button from '@/lib/components/ui/button/button.svelte';
  import Label from '@/lib/components/ui/label/label.svelte';
  import { Skeleton } from '$lib/components/ui/skeleton/index.js';
  import { Matchstick } from '@/Common';
  import Am5Loader from '@/Util/Am5Loader.svelte';
  import * as RadioGroup from '$lib/components/ui/radio-group/index.js';
  import Separator from '@/lib/components/ui/separator/separator.svelte';
  import DifficultyDiamonds from '@/DifficultyDiamonds.svelte';
  import { Slider } from '$lib/components/ui/slider/index.js';
  import * as Popover from '$lib/components/ui/popover/index.js';
  import MagicBucketsChart from '@/Activities/MagicBucketsChart.svelte';
  import { buttonVariants } from '@/lib/components/ui/button';
  import { magicBucketTypeInfo, type MagicBucketsFieldsAndData } from './MagicBucketsFieldsAndData';

  export let fields: MagicBucketsFieldsAndData;
  export let targetsAreLowIntensity = false;

</script>

{#if targetsAreLowIntensity}
  <div class="text-muted-foreground mt-4">This creates a basic Endurance workout for you to use to perform your training.</div>
{:else}
  <div class="text-muted-foreground mt-4">{magicBucketTypeInfo["workout"].description}</div>
{/if}
<Separator class="mt-4 mb-6" orientation="horizontal" />

{#if fields.magicIntervalData}
  <div class="space-y-3">
    <div class="font-medium">Challenge Level</div>
    <RadioGroup.Root
      class="flex gap-4 flex-wrap"
      value={fields.selectedChallengeLevel?.toString()}
      onValueChange={(v) => {
        // setIntervalDuration(Math.round(baseIntervalDuration * 1.5))
        fields.selectMagicInterval(Number(v));
      }}
    >
      {#each fields.magicIntervalData as mpaData}
        <Label class="flex items-center justify-center text-center rounded-xl w-14 h-10 border-solid border-2 border-muted mb-0 hover:text-accent-foreground hover:bg-accent [&:has(button[data-state='checked'])]:border-primary">
          <div class="">{mpaData.summary.cl}</div>
          <RadioGroup.Item class="sr-only" value={mpaData.summary.cl.toString()} />
        </Label>
      {/each}
    </RadioGroup.Root>
  </div>

  <div class="space-y-3 mt-8">
    <span class="font-medium">Workout Details</span>
    {#await fields.magicIntervalChartData}
      <div class="flex gap-4">
        <Skeleton class="w-4" />
        <div class="flex flex-wrap gap-6">
          <Skeleton class="h-20 w-52" />
          <Skeleton class="h-20 w-52" />
        </div>
      </div>
    {:then mpaData}
      {#if mpaData != null}
        {@const summary = mpaData.summary}
        <div class="flex gap-6">
          <Matchstick width={15} xss={summary.xss} xlss={summary.xlss} xhss={summary.xhss} xpss={summary.xpss} />

          <div class="flex flex-wrap gap-x-8 gap-y-1">
            <div class="flex flex-col gap-1">
              <div>Duration: <span class="font-medium">{format_duration(summary.duration)}</span></div>
              <div>
                <span>Target XSS: </span>
                <span class="font-medium">
                  {round(fields.targets.xss)}
                  <span class="whitespace-nowrap">
                    {'('}
                    <span class="text-xertlow">{round(fields.targets.xlss)}</span>
                    {' | '}
                    <span class="text-xerthigh">{round(fields.targets.xhss, 1)}</span>
                    {' | '}
                    <span class="text-xertpeak">{round(fields.targets.xpss, 1)}</span>
                    {')'}
                  </span>
                </span>
              </div>
              <div>
                <span>Workout XSS: </span>
                <span class="font-medium">
                  {round(summary.xss)}
                  <span class="whitespace-nowrap">
                    {'('}
                    <span class="text-xertlow">{round(summary.xlss)}</span>
                    {' | '}
                    <span class="text-xerthigh">{round(summary.xhss, 1)}</span>
                    {' | '}
                    <span class="text-xertpeak">{round(summary.xpss, 1)}</span>
                    {')'}
                  </span>
                </span>
              </div>
            </div>
            <div class="flex flex-col gap-1">
              <div>Difficulty: <span class="font-medium"><DifficultyDiamonds compact difficulty={summary.xds} /> - {round(summary.xds)}</span></div>
              <div>Interval Power: <span class="font-medium">{round(mpaData.summary.intervalPower, 0)}W</span></div>
              <div class="flex gap-1 items-center">
                <div>Interval Duration:</div>
                <span class="font-medium">{round(fields.intervalDuration)}s</span>
                <Popover.Root portal="#shadcn-ui-dialog-target">
                  <Popover.Trigger class={cn(buttonVariants({ variant: "ghost", size: 'icon' }))}>
                    <i class="fa-edit fa-regular ml-1" />
                  </Popover.Trigger>
                  <Popover.Content class="flex flex-col gap-4">
                    <div class="flex justify-between">
                      <div class="text-card-foreground font-medium">Interval Duration</div>
                      <div class="text-base">{fields.sliderIntervalDuration}s</div>
                    </div>
                    <Slider
                      min={Math.round(mpaData.summary.baseIntervalDuration * 1)}
                      max={Math.round(mpaData.summary.baseIntervalDuration * 9)}
                      step={1}
                      value={[fields.sliderIntervalDuration]}
                      onValueChange={(duration) => {
                        fields.sliderIntervalDuration = duration[0];
                      }}
                    />
                    <div class="flex justify-end gap-2">
                      <Button
                        size="sm"
                        variant="secondary"
                        on:click={() => {
                          fields.sliderIntervalDuration = Math.round(fields.baseIntervalDuration * 1.5);
                        }}
                      >
                        Reset
                      </Button>
                      <Popover.Close asChild let:builder>
                        <Button
                          size="sm"
                          builders={[builder]}
                          on:click={() => {
                            fields.setIntervalDuration(fields.sliderIntervalDuration);
                            fields.generateMagicIntervals();
                          }}
                        >
                          Update
                        </Button>
                      </Popover.Close>
                    </div>
                  </Popover.Content>
                </Popover.Root>
              </div>
            </div>
          </div>
        </div>
      {/if}
    {/await}
  </div>

  {#await fields.magicIntervalChartData}
    <Skeleton class="w-full aspect-[3/1] mt-10" />
  {:then mpaData}
    <div class="mt-10">
      <Am5Loader>
        <MagicBucketsChart data={mpaData} />
      </Am5Loader>
    </div>
  {/await}
{/if}
